import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography } from '@guildeducationinc/recess';
import ExpandLessIcon from '@guildeducationinc/recess/icons/ExpandLessIcon';
import ExpandMoreIcon from '@guildeducationinc/recess/icons/ExpandMoreIcon';
import { ActivityInfo } from '@src/components/Employee/ActivityInfo/ActivityInfo';
import { ViewPaymentRequest } from '@src/components/Employee/PaymentRequests/ViewPaymentRequest';
import { ViewProgram } from '@src/components/Employee/Programs/ViewProgram';
import { PaymentRequestStatusTag } from '@src/components/PaymentRequestStatusTag';
import { ProgramStatusTag } from '@src/components/ProgramStatusTag';
import { ProgramContextProvider } from '@src/contexts/ProgramContextProvider';
import { useCurrentCurrency } from '@src/hooks/useCurrentCurrency';
import { PaymentMethod, PaymentRequestStatus } from '@src/types';
import { getType } from '@src/utilities/activityHelper';
import { getFullName } from '@src/utilities/nameHelper';
import { getSchoolName } from '@src/utilities/programHelper';
import { ArrowRightIcon } from 'lucide-react';
import { TrackableButton, trackClickEvent, useBreakpoints, useLocale } from 'pe-core-frontend';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './ActivityRow.module.scss';
export const ActivityRow = ({ activity, adminView, showEmployee }) => {
    const currency = useCurrentCurrency({ clientId: activity.paymentRequest?.clientId });
    const location = useLocation();
    const navigate = useNavigate();
    const { isMobile } = useBreakpoints();
    const { t, formatToLocalDatetime, formatCurrency } = useLocale();
    const [expanded, setExpanded] = useState(false);
    const { isProgram, isPaymentRequest } = getType(activity);
    const program = activity.program;
    let amountDisplay = '';
    let amountRequestedDisplay = '';
    const tag = `activity_${activity.id}`;
    if (isPaymentRequest) {
        const paymentRequest = activity.paymentRequest;
        // Amount requested display
        if (paymentRequest?.amountRequested !== undefined) {
            amountRequestedDisplay = formatCurrency(paymentRequest.amountRequested, currency, true);
        }
        else {
            amountRequestedDisplay = t('N/A');
        }
        // Amount paid display
        if ([
            PaymentRequestStatus.READY_FOR_PAYMENT,
            PaymentRequestStatus.CLOSED,
            PaymentRequestStatus.APPROVED,
            PaymentRequestStatus.EXPIRED,
        ].includes(paymentRequest?.status ?? PaymentRequestStatus.PENDING)) {
            amountDisplay = formatCurrency(paymentRequest?.amountToPay || 0, currency, true);
        }
        else if (paymentRequest?.status === PaymentRequestStatus.REJECTED) {
            amountDisplay = formatCurrency(0, currency, true);
        }
        else {
            amountDisplay = t('Processing');
        }
    }
    else {
        amountRequestedDisplay = t('N/A');
        amountDisplay = t('N/A');
    }
    const handleClick = () => {
        if (adminView) {
            trackClickEvent(expanded ? 'collapse' : 'expand', tag, 'button');
            setExpanded(prev => !prev);
            return;
        }
        let url = '';
        if (isProgram) {
            url = `/programs/${program.id}?returnTo=${location.pathname}`;
        }
        else {
            url = `/payments/${activity.paymentRequest.id}?returnTo=${location.pathname}`;
        }
        trackClickEvent('view_details', tag, 'button', t('View details'), { url });
        navigate(url);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleClick();
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs("tr", { role: 'button', onKeyDown: handleKeyDown, onClick: handleClick, className: 'cursor-pointer', children: [showEmployee && (_jsx("td", { children: _jsx(Typography, { children: activity.program.employee ? getFullName(activity.program.employee) : '-' }) })), (isMobile && (_jsx(_Fragment, { children: _jsx("td", { children: _jsx(ActivityInfo, { activity: activity, showUpdatedAt: true }) }) }))) || (_jsxs(_Fragment, { children: [_jsx("td", { children: _jsx(Typography, { className: 'whitespace-nowrap', children: formatToLocalDatetime(activity.updatedAt, { dateStyle: 'medium' }) }) }), _jsx("td", { children: isProgram ? (_jsx(ProgramStatusTag, { status: activity.program.status })) : (activity.paymentRequest && (_jsx(PaymentRequestStatusTag, { paymentRequest: activity.paymentRequest }))) }), _jsx("td", { children: _jsx(Typography, { children: isPaymentRequest
                                        ? activity.paymentRequest?.paymentMethod == PaymentMethod.PREIMBURSEMENT
                                            ? t('Advance payment')
                                            : t('Reimbursement request')
                                        : t('Program application') }) }), _jsxs("td", { children: [_jsx(Typography, { weight: '700', children: t('{program} at {school}', {
                                            values: { program: program.programName, school: getSchoolName(program) },
                                        }) }), _jsx("br", {}), _jsx(Typography, { children: program.programLevelName })] }), _jsx("td", { className: 'whitespace-nowrap', children: amountRequestedDisplay }), _jsx("td", { className: 'whitespace-nowrap', children: amountDisplay })] })), _jsx("td", { style: { height: 1 }, children: _jsxs("div", { className: styles.actions, children: [isMobile && (_jsx("div", { children: isProgram ? (_jsx(ProgramStatusTag, { status: activity.program.status })) : (activity.paymentRequest && (_jsx(PaymentRequestStatusTag, { paymentRequest: activity.paymentRequest }))) })), adminView ? (_jsx(TrackableButton, { btnType: 'icon', enText: expanded ? 'Collapse' : 'Expand', tag: `activity_${activity.id}`, appearance: 'minimal', label: expanded ? t('Collapse') : t('Expand'), icon: expanded ? _jsx(ExpandLessIcon, { size: 20 }) : _jsx(ExpandMoreIcon, { size: 20 }) })) : (_jsx(TrackableButton, { btnType: 'icon', enText: 'View Details', tag: `activity_${activity.id}`, appearance: 'minimal', label: t('View Details'), icon: _jsx(ArrowRightIcon, { size: 20 }) }))] }) })] }), adminView && expanded && (_jsx("tr", { children: _jsxs("td", { colSpan: isMobile ? 2 : 7, className: styles.box, children: [activity.paymentRequest && _jsx(ViewPaymentRequest, { id: activity.paymentRequest.id }), isProgram && (_jsx("div", { className: 'flex flex-col gap-3', children: _jsx(ProgramContextProvider, { program: activity.program, children: _jsx(ViewProgram, { program: activity.program, paymentRequestId: activity.paymentRequest?.id }) }) }))] }) }))] }));
};
